/**
 * Object snapping.
 *
 * CIRCAD relies on symbols being drawn on-grid: set the grid to .1" and a
 * TTL gate's signal pins land on it. That breaks down at the edges — a 7404's
 * power pins sit at ±.125", off any .1" grid — and a wire snapped to the grid
 * then misses the pin by .025" and forms no connection. The netlist is right
 * to reject it; the drawing simply never touched the pad.
 *
 * So this is a modernisation, not a port: the cursor prefers a real connection
 * point over a grid intersection when one is close enough. It deliberately
 * uses the same connection points the netlist does, so anything you can snap
 * to is something that will actually connect.
 */

import { type CadDocument, type ComponentEntity, type Entity, type Vertex, padIsThrough } from '../model.ts';
import { placeInComponent, refDesOf } from '../net/netlist.ts';
import { padThrough } from './pad.ts';

export type SnapKind = 'pad' | 'vertex' | 'grid';

export interface SnapResult {
  x: number;
  y: number;
  kind: SnapKind;
  /** Entity the point belongs to, when it is not a grid point. */
  entityId?: number;
  /** Pin number, for a pad. */
  pin?: string;
  /** Net the target already carries, if any. */
  signal?: string;
  /** Reference designator of the owning component, for a pad. */
  ref?: string;
  /** The pad's own layer, so a track can route onto a layer that connects. */
  padLayer?: number;
  /** True for a surface-mount pad, which lives on one copper layer only. */
  padSurface?: boolean;
}

/**
 * Connection points of a document, in document space.
 *
 * Pads first: a pin is what you almost always mean, and ordering the
 * candidates puts it ahead of a coincident line endpoint at equal distance.
 */
export function snapCandidates(doc: CadDocument): SnapResult[] {
  const pads: SnapResult[] = [];
  const vertices: SnapResult[] = [];

  const visit = (list: Entity[], owner: ComponentEntity | null): void => {
    for (const e of list) {
      if (e.kind === 'component') { visit(e.children, e); continue; }

      const at = (p: Vertex): Vertex => (owner ? placeInComponent(p, owner) : p);

      if (e.kind === 'pad') {
        const p = at({ x: e.x, y: e.y });
        const hit: SnapResult = { x: p.x, y: p.y, kind: 'pad', entityId: e.id };
        if (e.pin) hit.pin = e.pin;
        if (e.signal) hit.signal = e.signal;
        if (owner) hit.ref = refDesOf(owner);
        hit.padLayer = e.layer;
        // A surface pad lives on one copper layer, so a trace must be on that
        // same layer to connect — unlike a through pad, which reaches all.
        hit.padSurface = !padThrough(e);
        pads.push(hit);
        continue;
      }

      if (e.kind === 'line') {
        for (const p of [{ x: e.x1, y: e.y1 }, { x: e.x2, y: e.y2 }]) {
          const q = at(p);
          const hit: SnapResult = { x: q.x, y: q.y, kind: 'vertex', entityId: e.id };
          if (e.signal) hit.signal = e.signal;
          vertices.push(hit);
        }
        continue;
      }

      if (e.kind === 'arc') {
        const a1 = (e.angle * Math.PI) / 180;
        const a2 = ((e.angle + e.sweep) * Math.PI) / 180;
        for (const p of [
          { x: e.cx + e.radius * Math.cos(a1), y: e.cy + e.radius * Math.sin(a1) },
          { x: e.cx + e.radius * Math.cos(a2), y: e.cy + e.radius * Math.sin(a2) },
        ]) {
          const q = at(p);
          const hit: SnapResult = { x: q.x, y: q.y, kind: 'vertex', entityId: e.id };
          if (e.signal) hit.signal = e.signal;
          vertices.push(hit);
        }
      }
    }
  };

  visit(doc.entities, null);
  return [...pads, ...vertices];
}

export interface SnapOptions {
  /** Search radius in document units. Callers derive this from screen pixels. */
  tolerance: number;
  /** Grid to fall back to; 0 disables grid snapping. */
  grid?: number;
  /** Restrict to pads — used when only a pin is a sensible target. */
  padsOnly?: boolean;
  /** Ignore these entities, e.g. the segment currently being drawn. */
  exclude?: ReadonlySet<number>;
  /** Pre-computed candidates, to avoid rebuilding them every mouse move. */
  candidates?: readonly SnapResult[];
}

/**
 * Snap a cursor position.
 *
 * A connection point within `tolerance` wins; otherwise the grid does. The
 * returned `kind` lets the caller show which happened, because silently
 * moving the cursor somewhere the user did not click is only acceptable if
 * they can see it.
 */
export function snapPoint(
  doc: CadDocument,
  x: number,
  y: number,
  opts: SnapOptions,
): SnapResult {
  const candidates = opts.candidates ?? snapCandidates(doc);
  const limit = opts.tolerance * opts.tolerance;

  let best: SnapResult | null = null;
  let bestDist = Infinity;

  for (const c of candidates) {
    if (opts.padsOnly && c.kind !== 'pad') continue;
    if (c.entityId !== undefined && opts.exclude?.has(c.entityId)) continue;
    const d = (c.x - x) ** 2 + (c.y - y) ** 2;
    // Strictly closer, so the pad-first ordering breaks ties toward pins.
    if (d < bestDist && d <= limit) { bestDist = d; best = c; }
  }

  if (best) return best;

  const g = opts.grid ?? 0;
  if (g > 0) {
    return { x: Math.round(x / g) * g, y: Math.round(y / g) * g, kind: 'grid' };
  }
  return { x, y, kind: 'grid' };
}
